/**
 * RecurringReminderPicker — purpose-built reminder offset picker for recurring tasks.
 *
 * Unlike the regular ReminderSection (which picks absolute times), this picker
 * works entirely in OFFSET mode relative to each occurrence's scheduled time.
 *
 * Two modes:
 * - Quick: Grid of before/after presets (2 per row), select then save
 * - Custom: Hour + minute + before/after button group
 *
 * Footer: Cancel + Grouped [kind-toggle | save] button (same pattern as ReminderSection)
 */

import { memo, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { cn } from "@/shared/utils/general/cn"
import { ReminderKind } from "@/db/schema"
import { ResponsiveSheet } from "@/app/components/ui/responsive-sheet"
import BellIcon from "@/shared/icons/pikaicons/stroke/alerts/notification-bell-on.svg?react"
import AlarmIcon from "@/shared/icons/pikaicons/stroke/time/alarm-default.svg?react"
import LightningIcon from "@/shared/icons/pikaicons/stroke/weather/lightning-thunder-electric-on.svg?react"
import SettingsIcon from "@/shared/icons/totodoicons/stroke/settings.svg?react"
import BeforeIcon from "@/shared/icons/pikaicons/stroke/time/reminder-anticlockwise.svg?react"
import AfterIcon from "@/shared/icons/pikaicons/stroke/time/reminder-clockwise.svg?react"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ReminderOffset {
  offsetMs: number
  kind: "reminder" | "alarm"
}

interface RecurringReminderPickerProps {
  /** The time of day the recurring task is scheduled (e.g. "09:00") as HH:mm */
  taskTimeLabel?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (offset: ReminderOffset) => void
}

// ---------------------------------------------------------------------------
// Offset presets — all relative to task scheduled time
// ---------------------------------------------------------------------------

const MS_MINUTE = 60_000
const MS_HOUR = 60 * MS_MINUTE
const MS_DAY = 24 * MS_HOUR

interface OffsetPreset {
  key: string
  label: string
  offsetMs: number
  section: "before" | "after"
}

function getOffsetPresets(t: (k: string, fallback: string) => string, taskTimeLabel?: string): OffsetPreset[] {
  const timeStr = taskTimeLabel ?? "scheduled time"
  return [
    // Before (including "at time")
    { key: "at_time", label: t("recurrence.rem.atTime", `At ${timeStr}`), offsetMs: 0, section: "before" },
    { key: "15m_before", label: t("recurrence.rem.15min", "15 min before"), offsetMs: -15 * MS_MINUTE, section: "before" },
    { key: "30m_before", label: t("recurrence.rem.30min", "30 min before"), offsetMs: -30 * MS_MINUTE, section: "before" },
    { key: "1h_before", label: t("recurrence.rem.1h", "1 hour before"), offsetMs: -1 * MS_HOUR, section: "before" },
    { key: "morning", label: t("recurrence.rem.morning", "Morning of (07:00)"), offsetMs: -getOffsetToMorning(taskTimeLabel), section: "before" },
    { key: "1d_before", label: t("recurrence.rem.1day", "1 day before"), offsetMs: -1 * MS_DAY, section: "before" },
    // After
    { key: "15m_after", label: t("recurrence.rem.15after", "15 min after"), offsetMs: 15 * MS_MINUTE, section: "after" },
    { key: "1h_after", label: t("recurrence.rem.1hAfter", "1 hour after"), offsetMs: 1 * MS_HOUR, section: "after" },
    { key: "2h_after", label: t("recurrence.rem.2hAfter", "2 hours after"), offsetMs: 2 * MS_HOUR, section: "after" },
    { key: "1d_after", label: t("recurrence.rem.1dayAfter", "1 day after"), offsetMs: 1 * MS_DAY, section: "after" },
  ]
}

/** Calculate offset from taskTime to 07:00 same day. If task is before 07:00, returns 0. */
function getOffsetToMorning(taskTimeLabel?: string): number {
  if (!taskTimeLabel) return 2 * MS_HOUR // default: 2h before
  const [h, m] = taskTimeLabel.split(":").map(Number)
  if (h == null || m == null) return 2 * MS_HOUR
  const taskMinutes = h * 60 + m
  const morningMinutes = 7 * 60
  const diff = (taskMinutes - morningMinutes) * MS_MINUTE
  return diff > 0 ? diff : 0
}

// ---------------------------------------------------------------------------
// Format offset for display
// ---------------------------------------------------------------------------

export function formatReminderOffset(offsetMs: number): string {
  const abs = Math.abs(offsetMs)
  const direction = offsetMs < 0 ? "before" : offsetMs > 0 ? "after" : ""

  if (offsetMs === 0) return "At task time"

  if (abs < MS_HOUR) {
    const mins = Math.round(abs / MS_MINUTE)
    return `${mins} min ${direction}`
  }
  if (abs < MS_DAY) {
    const hours = abs / MS_HOUR
    if (hours === Math.floor(hours)) {
      return `${hours}h ${direction}`
    }
    const h = Math.floor(hours)
    const m = Math.round((hours - h) * 60)
    return m > 0 ? `${h}h ${m}min ${direction}` : `${h}h ${direction}`
  }

  const days = abs / MS_DAY
  if (days === Math.floor(days)) {
    const d = Math.floor(days)
    return `${d} day${d > 1 ? "s" : ""} ${direction}`
  }

  const d = Math.floor(days)
  const remainingHours = Math.round((days - d) * 24)
  if (remainingHours > 0) {
    return `${d}d ${remainingHours}h ${direction}`
  }
  return `${d} day${d > 1 ? "s" : ""} ${direction}`
}

/** Format with time-of-day context for display in the list */
export function formatReminderOffsetWithTime(offsetMs: number, taskTimeLabel?: string): string {
  if (!taskTimeLabel) return formatReminderOffset(offsetMs)

  const [h, m] = taskTimeLabel.split(":").map(Number)
  if (h == null || m == null) return formatReminderOffset(offsetMs)

  const taskMinutes = h * 60 + m
  const reminderMinutes = taskMinutes + offsetMs / MS_MINUTE
  const reminderH = Math.floor(((reminderMinutes % (24 * 60)) + 24 * 60) % (24 * 60) / 60)
  const reminderM = Math.round(((reminderMinutes % 60) + 60) % 60)
  const timeStr = `${String(reminderH).padStart(2, "0")}:${String(reminderM).padStart(2, "0")}`

  const abs = Math.abs(offsetMs)
  if (offsetMs === 0) return `At task time \u00b7 ${timeStr}`
  if (abs < MS_DAY) return `${formatReminderOffset(offsetMs)} \u00b7 ${timeStr}`

  const days = Math.floor(abs / MS_DAY)
  const direction = offsetMs < 0 ? "before" : "after"
  return `${days} day${days > 1 ? "s" : ""} ${direction} \u00b7 ${timeStr}`
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const RecurringReminderPicker = memo(function RecurringReminderPicker({
  taskTimeLabel,
  open,
  onOpenChange,
  onSelect,
}: RecurringReminderPickerProps) {
  const { t } = useTranslation()
  const [kind, setKind] = useState<ReminderKind>(() => {
    const stored = localStorage.getItem("totodo:reminder-kind")
    return stored === String(ReminderKind.Alarm) ? ReminderKind.Alarm : ReminderKind.Reminder
  })
  const [mode, setMode] = useState<"quick" | "custom">("quick")
  const [selectedPreset, setSelectedPreset] = useState<string>("at_time")
  const [selectedOffsetMs, setSelectedOffsetMs] = useState<number>(0)
  const [customHours, setCustomHours] = useState(1)
  const [customMinutes, setCustomMinutes] = useState(0)
  const [customDirection, setCustomDirection] = useState<"before" | "after">("before")

  const presets = useMemo(() => getOffsetPresets(t, taskTimeLabel), [t, taskTimeLabel])
  const beforePresets = useMemo(() => presets.filter(p => p.section === "before"), [presets])
  const afterPresets = useMemo(() => presets.filter(p => p.section === "after"), [presets])

  // Filter out "Morning of" if it returns 0 (task is before 07:00)
  const filteredBefore = useMemo(() => {
    return beforePresets.filter(p => !(p.key === "morning" && p.offsetMs === 0))
  }, [beforePresets])

  const handlePresetSelect = useCallback((preset: OffsetPreset) => {
    setSelectedPreset(preset.key)
    setSelectedOffsetMs(preset.offsetMs)
  }, [])

  const handleSave = useCallback(() => {
    const offsetMs = mode === "custom"
      ? (customHours * MS_HOUR + customMinutes * MS_MINUTE) * (customDirection === "before" ? -1 : 1)
      : selectedOffsetMs
    const kindStr = kind === ReminderKind.Alarm ? "alarm" as const : "reminder" as const
    onSelect({ offsetMs, kind: kindStr })
    onOpenChange(false)
  }, [mode, customHours, customMinutes, customDirection, selectedOffsetMs, kind, onSelect, onOpenChange])

  const toggleKind = useCallback(() => {
    setKind(prev => {
      const next = prev === ReminderKind.Alarm ? ReminderKind.Reminder : ReminderKind.Alarm
      localStorage.setItem("totodo:reminder-kind", String(next))
      return next
    })
  }, [])

  const actionButtonLabel = useMemo(() => {
    if (mode === "custom") {
      return formatReminderOffset(
        (customHours * MS_HOUR + customMinutes * MS_MINUTE) * (customDirection === "before" ? -1 : 1),
      )
    }
    const preset = presets.find(p => p.key === selectedPreset)
    return preset ? preset.label : t("recurrence.rem.save", "Save")
  }, [mode, customHours, customMinutes, customDirection, selectedPreset, presets, t])

  // Dynamic title: "Remind me 15 min before" / "Alarm 1h after"
  const sheetTitle = useMemo(() => {
    const prefix = kind === ReminderKind.Alarm
      ? t("recurrence.rem.alarmPrefix", "Alarm")
      : t("recurrence.rem.remindPrefix", "Remind me")
    return `${prefix} ${actionButtonLabel.toLowerCase()}`
  }, [kind, actionButtonLabel, t])

  // Compute the actual fire time based on current offset + task time
  const fireTimeLabel = useMemo(() => {
    if (!taskTimeLabel) return undefined
    const [h, m] = taskTimeLabel.split(":").map(Number)
    if (h == null || m == null) return undefined
    const currentOffset = mode === "custom"
      ? (customHours * MS_HOUR + customMinutes * MS_MINUTE) * (customDirection === "before" ? -1 : 1)
      : selectedOffsetMs
    const taskMinutes = h * 60 + m
    const fireMinutes = taskMinutes + currentOffset / MS_MINUTE
    const normalized = ((fireMinutes % (24 * 60)) + 24 * 60) % (24 * 60)
    const fireH = Math.floor(normalized / 60)
    const fireM = Math.round(normalized % 60)
    return `${String(fireH).padStart(2, "0")}:${String(fireM).padStart(2, "0")}`
  }, [taskTimeLabel, mode, customHours, customMinutes, customDirection, selectedOffsetMs])

  return (
    <ResponsiveSheet open={open} onOpenChange={onOpenChange} title={sheetTitle}>
      <div className="flex flex-col overflow-y-auto px-4">
        {/* Dynamic title — "Remind me 15 min before" */}
        <h2 className="pb-5 pt-3 text-center text-xl font-bold">
          {sheetTitle}
        </h2>

        {/* Subtitle: relative to task time + fire time */}
        {taskTimeLabel && (
          <p className="-mt-3 mb-4 text-center text-sm text-muted-foreground">
            {t("recurrence.rem.relativeTo", "Relative to task time")}
            {" "}
            <span className="font-semibold text-foreground">{taskTimeLabel}</span>
            {fireTimeLabel && fireTimeLabel !== taskTimeLabel && (
              <>
                {" · "}
                {t("recurrence.rem.firesAt", "fires at")}
                {" "}
                <span className="font-semibold text-foreground">{fireTimeLabel}</span>
              </>
            )}
          </p>
        )}

        {/* Quick mode: preset grids */}
        {mode === "quick" && (
          <div className="flex flex-col gap-4">
            {/* Before presets */}
            <div>
              <div className="flex items-center gap-1.5 px-1 pb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                <BeforeIcon className="size-3.5" />
                {t("recurrence.rem.beforeSection", "Before")}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {filteredBefore.map(preset => (
                  <button
                    key={preset.key}
                    type="button"
                    onClick={() => handlePresetSelect(preset)}
                    className={cn(
                      "rounded-2xl px-3 py-2.5 text-[13px] font-medium",
                      "transition-all active:scale-[0.97]",
                      selectedPreset === preset.key
                        ? "bg-primary/10 text-primary"
                        : "bg-muted text-foreground hover:bg-muted/80",
                    )}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* After presets */}
            <div>
              <div className="flex items-center gap-1.5 px-1 pb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                <AfterIcon className="size-3.5" />
                {t("recurrence.rem.afterSection", "After")}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {afterPresets.map(preset => (
                  <button
                    key={preset.key}
                    type="button"
                    onClick={() => handlePresetSelect(preset)}
                    className={cn(
                      "rounded-2xl px-3 py-2.5 text-[13px] font-medium",
                      "transition-all active:scale-[0.97]",
                      selectedPreset === preset.key
                        ? "bg-primary/10 text-primary"
                        : "bg-muted text-foreground hover:bg-muted/80",
                    )}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Custom mode: h + min + before/after — single row */}
        {mode === "custom" && (
          <div className="flex items-center justify-center gap-2 py-4">
            <input
              type="number"
              min={0}
              max={72}
              value={customHours}
              onChange={e => setCustomHours(Math.max(0, Number(e.target.value)))}
              className="w-14 rounded-md border px-2 py-2.5 text-center text-sm"
            />
            <span className="text-sm text-muted-foreground">h</span>
            <input
              type="number"
              min={0}
              max={59}
              step={5}
              value={customMinutes}
              onChange={e => setCustomMinutes(Math.max(0, Math.min(59, Number(e.target.value))))}
              className="w-14 rounded-md border px-2 py-2.5 text-center text-sm"
            />
            <span className="text-sm text-muted-foreground">min</span>

            {/* Before / After toggle */}
            <div className="inline-flex overflow-hidden rounded-lg bg-muted p-0.5">
              <button
                type="button"
                onClick={() => setCustomDirection("before")}
                className={cn(
                  "rounded-md px-3 py-2 text-xs font-medium transition-all",
                  customDirection === "before"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t("recurrence.rem.before", "Before")}
              </button>
              <button
                type="button"
                onClick={() => setCustomDirection("after")}
                className={cn(
                  "rounded-md px-3 py-2 text-xs font-medium transition-all",
                  customDirection === "after"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t("recurrence.rem.after", "After")}
              </button>
            </div>
          </div>
        )}

        {/* Segmented control: Quick / Custom — below content */}
        <div className="flex items-center justify-center pt-4 pb-1">
          <div className="inline-flex rounded-xl bg-muted p-1">
            <button
              type="button"
              onClick={() => setMode("quick")}
              className={cn(
                "flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium transition-all",
                mode === "quick"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <LightningIcon className="size-3.5" />
              {t("recurrence.rem.quickSelect", "Quick")}
            </button>
            <button
              type="button"
              onClick={() => setMode("custom")}
              className={cn(
                "flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium transition-all",
                mode === "custom"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <SettingsIcon className="size-3.5" />
              {t("recurrence.rem.customMode", "Custom")}
            </button>
          </div>
        </div>
      </div>

      {/* Footer bar: Cancel | [Kind toggle + Save] grouped */}
      <div className="mt-4 flex items-center gap-3 border-t border-border/30 px-4 pt-3 pb-2">
        {/* Cancel */}
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          {t("Cancel")}
        </button>

        {/* Grouped button: [Kind toggle | Save] */}
        <div className="flex flex-1 overflow-hidden rounded-lg">
          {/* Kind toggle (left segment) */}
          <button
            type="button"
            onClick={toggleKind}
            title={kind === ReminderKind.Reminder ? t("Switch to alarm") : t("Switch to reminder")}
            className={cn(
              "flex items-center justify-center px-3 py-2.5 transition-colors",
              "bg-primary text-primary-foreground hover:bg-primary/90",
            )}
          >
            {kind === ReminderKind.Reminder ? (
              <BellIcon className="size-5" />
            ) : (
              <AlarmIcon className="size-5" />
            )}
          </button>

          {/* Save button (right segment) */}
          <button
            type="button"
            onClick={handleSave}
            className={cn(
              "flex-1 border-l border-primary-foreground/20 bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground",
              "transition-colors hover:bg-primary/90",
            )}
          >
            {actionButtonLabel}
          </button>
        </div>
      </div>

      {/* Safe area spacer for iOS/Android */}
      <div className="pb-4" />
      <div className="safe-area-bottom" />
    </ResponsiveSheet>
  )
})
